import { useContext, useLayoutEffect } from 'react';
import type { BreadcrumbItem } from '@/shared/ui/primitives';
import { BreadcrumbContext } from './breadcrumb-context';

/** Reads the current breadcrumb trail. Returns `[]` outside a provider. */
export function useBreadcrumbItems(): BreadcrumbItem[] {
  return useContext(BreadcrumbContext)?.items ?? [];
}

/**
 * Publishes a breadcrumb trail to the top bar for the lifetime of the calling
 * screen, clearing it on unmount. A no-op outside a `BreadcrumbProvider` so
 * screens stay usable in isolation (tests, storybook). Runs as a layout effect
 * so the trail is set before paint — no flash of the previous page's crumbs on
 * navigation.
 */
export function useSetBreadcrumb(items: BreadcrumbItem[]): void {
  const setItems = useContext(BreadcrumbContext)?.setItems;

  // `items` is a fresh array each render; key the effect on the trail's content
  // (labels are i18n strings in practice) so it only re-publishes on real
  // changes and never loops.
  const trailKey = items
    .map((item) => `${item.to ?? ''}::${typeof item.label === 'string' ? item.label : ''}`)
    .join('>>');

  useLayoutEffect(() => {
    if (!setItems) return;
    setItems(items);
    return () => setItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on trailKey, not the array identity
  }, [setItems, trailKey]);
}
